import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../firebaseConfig";
// Firestore imports removed
import { ApiService } from "../../services/api_service";
import loginImage from "../../Assets/login_pagedisplay.jpg";
import fusshnLogo from "../../Assets/fusshn_logo.svg";
import OTPVerification from "../../common_components/OTPVerification";

const Login = () => {
  const navigate = useNavigate();



  // Form state
  const [formData, setFormData] = useState({
    mobile: ""
  });

  // UI state
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [authMode, setAuthMode] = useState('mobile'); // 'mobile' or 'email'

  // Derived UI validation states
  const isMobileValid = formData.mobile && /^[6-9]\d{9}$/.test(formData.mobile);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear general error when user types
    if (errors.general) {
      setErrors(prev => ({
        ...prev,
        general: ''
      }));
    }
  };

  // Validation functions
  const validateMobile = (mobile) => {
    const mobileRegex = /^[6-9]\d{9}$/;
    return mobileRegex.test(mobile);
  };

  const validateForm = () => {
    const newErrors = {};

    // Mobile validation
    if (!formData.mobile) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!validateMobile(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // For mobile authentication, we'll proceed to OTP verification
      setShowOTPVerification(true);
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP verification success
  const handleOTPSuccess = async (user) => {
    try {
      // Check if user exists via API
      let userExists = false;
      try {
        const response = await ApiService.getSingleUser(user.uid);
        const userData = response.data || response;
        if (userData && userData.uid) {
          userExists = true;
        }
      } catch (err) {
        // Assume 404 or similar means user doesn't exist
        // But need to be careful if it's a network error. 
        // For now, assuming if fetch fails, we might need to create or retry. 
        // Typical flow: check existence. 
        console.log("User might not exist, proceeding to check/create");
      }

      if (!userExists) {
        // Create user via API
        await ApiService.createUser({
          uid: user.uid,
          name: null,
          email: null,
          phone: `+91${formData.mobile}`,
          imageUrl: null,
          isEmailVerified: false,
          bookingIdsList: null,
          // createdAt handled by backend usually, but passing matched previous logic
        });
      }

      console.log("User signed in:", user);
      // Force reload to ensure App.js fetches the new user data
      window.location.href = "/";
    } catch (error) {
      console.error('Error handling OTP success:', error);
      setErrors({ general: 'Login successful but failed to update profile. Please try again.' });
    }
  };



  // Handle OTP verification error
  const handleOTPError = (error) => {
    console.error('OTP verification error:', error);
    setErrors({ general: error });
  };

  // Handle back from OTP verification
  const handleBackFromOTP = () => {
    setShowOTPVerification(false);
    setErrors({});
  };

  return (
    <div className="flex w-full h-screen max-h-screen overflow-hidden font-poppins bg-black text-white">
      {/* Left Side - Concert Image - Exactly 50% */}
      <div className="hidden lg:flex lg:w-1/2 h-full relative overflow-hidden bg-black">
        <img
          src={loginImage}
          alt="Concert Visual"
          className="w-full h-full object-cover"
          style={{ objectPosition: 'center' }}
        />
      </div>

      {/* Right Side - Login Form - Exactly 50% */}
      <div className="w-full lg:w-1/2 h-full flex items-center justify-center px-8 bg-black overflow-hidden">
        <div className="w-full max-w-[420px] flex flex-col" style={{ maxHeight: 'calc(100vh - 80px)', transform: 'scale(0.95)' }}>

          {/* Logo Section - Top Right */}
          <div className="absolute top-6 right-6 lg:top-8 lg:right-8 flex items-center gap-2">
            <img src={fusshnLogo} alt="FUSSHN Logo" className="w-7 h-7 lg:w-8 lg:h-8" />
            <span className="text-white text-base lg:text-lg font-medium uppercase">FUSSHN</span>
          </div>

          {/* Login Heading */}
          <h2 className="text-white text-[32px] lg:text-[36px] font-semibold mb-3" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            Login
          </h2>

          {/* General Error Message */}
          {errors.general && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{errors.general}</p>
            </div>
          )}

          {!showOTPVerification ? (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              {/* Mobile Number Field */}
              <div>
                <label className="block mb-2 text-[#CCCCCC] text-sm">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="Enter mobile number"
                  maxLength="10"
                  autoComplete="tel"
                  inputMode="numeric"
                  className="w-full py-3 px-4 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] text-white placeholder-gray-500 focus:outline-none focus:border-[#17BD3B]"
                />
                {errors.mobile && <p className="text-red-400 text-xs mt-2">{errors.mobile}</p>}
              </div>

              {/* Get OTP Button */}
              <button
                type="submit"
                disabled={loading || !isMobileValid}
                className={`w-full rounded-lg py-3 px-4 font-medium text-base ${loading || !isMobileValid
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-[#17BD3B] text-white hover:bg-[#15a834] transition-colors'
                  }`}
              >
                {loading ? 'Sending OTP...' : 'Get OTP'}
              </button>

              {/* Or Divider */}
              <div className="flex items-center my-2">
                <div className="flex-grow h-px bg-white/20" />
                <span className="px-4 text-white/60 text-sm">Or</span>
                <div className="flex-grow h-px bg-white/20" />
              </div>

              {/* Social Login Buttons */}
              <div className="flex gap-4">
                <button
                  type="button"
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg py-3 px-4 bg-white text-black hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 18 18" fill="none">
                    <path d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H15.0964C16.7036 14.2527 17.64 11.9454 17.64 9.20454Z" fill="#4285F4" />
                    <path d="M9 18C11.43 18 13.467 17.1941 14.9564 15.8195L11.9077 13.5614C11.0236 14.1014 9.87818 14.4204 9 14.4204C6.65454 14.4204 4.67182 12.8373 3.96409 10.71H0.852273V13.0418C2.37818 15.9832 5.40909 18 9 18Z" fill="#34A853" />
                    <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.852273C0.309091 6.17318 0 7.54773 0 9C0 10.4523 0.309091 11.8268 0.852273 13.0418L3.96409 10.71Z" fill="#FBBC05" />
                    <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.40909 0 2.37818 2.01682 0.852273 4.95818L3.96409 7.29C4.67182 5.16273 6.65454 3.57955 9 3.57955Z" fill="#EA4335" />
                  </svg>
                  <span className="text-sm">Continue with Google</span>
                </button>
                <button
                  type="button"
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg py-3 px-4 bg-white text-black hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                    <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="currentColor" />
                  </svg>
                  <span className="text-sm">Continue with Email</span>
                </button>
              </div>

              {/* Terms and Conditions - Inside Form */}
              <div className="flex flex-col items-center gap-1.5 mt-2">
                <p className="text-white/80 text-xs">By login you are agreeing to</p>
                <div className="flex gap-4 text-xs text-[#17BD3B]">
                  <Link to="/Terms & Conditions" className="hover:underline">Terms & Condition</Link>
                  <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
                </div>
              </div>
            </form>
          ) : (
            <OTPVerification
              phoneNumber={`+91${formData.mobile}`}
              onVerificationSuccess={handleOTPSuccess}
              onVerificationError={handleOTPError}
              onBack={handleBackFromOTP}
              isSignUp={false}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;